import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthContext';
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, TextField, MenuItem, Stack, Skeleton, InputAdornment, Tooltip, TableContainer,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { collection, getDocs, query, where, orderBy, limit, startAfter, doc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { CLP, FINANCE_COLORS, fmtFecha } from '../../utils/finance';

const { PRIMARY } = FINANCE_COLORS;
const ESTADO_COLOR = { borrador: 'default', enviado: 'info', aprobado: 'success', rechazado: 'error', vencido: 'warning', cobrado: 'primary', abonado: 'warning', pagado: 'success' };
const ESTADOS = ['borrador', 'enviado', 'aprobado', 'rechazado', 'vencido', 'cobrado', 'abonado', 'pagado'];
const ORIGEN_LABEL = {
  labOrder: 'Laboratorio', imagingOrder: 'Imágenes', vacuna: 'Vacuna', antiparasitario: 'Antiparasitario',
  surgeryRequest: 'Cirugía', hospitalizationOrder: 'Hospitalización', onlineBooking: 'Agenda',
};
const PAGE_SIZE = 50;

export default function PresupuestoList() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isAdmin = userData?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const { activeBranchId, isAllBranches, filterByBranch } = useBranch();

  const loadPage = useCallback(async (startAfterDoc = null, append = false) => {
    if (!startAfterDoc) setLoading(true);
    else setLoadingMore(true);

    const runQuery = async (q) => {
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      setRows((prev) => (append ? [...prev, ...docs] : docs));
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    };

    try {
      let baseQuery;
      if (!isAllBranches && activeBranchId && activeBranchId !== 'all') {
        // Filtra por sucursal en Firestore; requiere índice branchId + createdAt
        baseQuery = query(
          collection(db, 'presupuestos'),
          where('branchId', 'in', [activeBranchId, '']),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
      } else {
        baseQuery = query(collection(db, 'presupuestos'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
      }
      if (startAfterDoc) baseQuery = query(baseQuery, startAfter(startAfterDoc));

      try {
        await runQuery(baseQuery);
      } catch (branchErr) {
        // Fallback mientras se construye el índice compuesto: carga sin filtro de sucursal
        const fallback = startAfterDoc
          ? query(collection(db, 'presupuestos'), orderBy('createdAt', 'desc'), startAfter(startAfterDoc), limit(PAGE_SIZE))
          : query(collection(db, 'presupuestos'), orderBy('createdAt', 'desc'), limit(PAGE_SIZE));
        await runQuery(fallback);
      }
    } catch (_) {
      /* noop */
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeBranchId, isAllBranches]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleLoadMore = () => {
    if (lastDoc) loadPage(lastDoc, true);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return rows.filter((r) => {
      const matchQ = !q || (r.numero || '').toLowerCase().includes(q)
        || (r.patientName || '').toLowerCase().includes(q) || (r.ownerName || '').toLowerCase().includes(q);
      const matchE = !estado || r.estado === estado;
      const matchB = filterByBranch(r, { includeUnassigned: true });
      return matchQ && matchE && matchB;
    });
  }, [rows, search, estado, filterByBranch]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto? Esta acción no se puede deshacer.')) return;
    try {
      await deleteDoc(doc(db, 'presupuestos', id));
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error('Error eliminando presupuesto:', err);
      alert('No se pudo eliminar el presupuesto: ' + (err.message || err));
    }
  };

  const handleDeleteOldDrafts = async () => {
    if (!window.confirm('¿Eliminar todos los presupuestos en borrador con más de 45 días de antigüedad? Esta acción no se puede deshacer.')) return;
    setLoading(true);
    try {
      const deleteOld = httpsCallable(functions, 'deleteOldDraftPresupuestos');
      const result = await deleteOld();
      alert(`Se eliminaron ${result.data.deleted} presupuestos borradores antiguos.`);
      loadPage();
    } catch (err) {
      console.error('Error eliminando borradores antiguos:', err);
      alert('No se pudieron eliminar los borradores antiguos: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={800} color={PRIMARY}>Presupuestos</Typography>
          <Typography variant="body2" color="text.secondary">Cotizaciones de prestaciones para propietarios.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/presupuestos/nuevo')} sx={{ bgcolor: PRIMARY }}>
          Nuevo Presupuesto
        </Button>
        {isAdmin && (
          <Button variant="outlined" color="error" startIcon={<DeleteSweepIcon />} onClick={handleDeleteOldDrafts}>
            Eliminar borradores antiguos
          </Button>
        )}
      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2 }}>
          <TextField size="small" fullWidth placeholder="Buscar por N°, paciente o propietario…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos</MenuItem>
            {ESTADOS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>N°</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Origen</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Propietario</TableCell>
                <TableCell>Convenio</TableCell>
                <TableCell>Válido hasta</TableCell>
                <TableCell align="right">Desc.</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={11}><Skeleton height={26} /></TableCell></TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay presupuestos.
                </TableCell></TableRow>
              ) : filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.numero || '–'}</TableCell>
                  <TableCell>{fmtFecha(r.createdAt)}</TableCell>
                  <TableCell>{r.orderNumber || ORIGEN_LABEL[r.orderType] || (r.orderType ? r.orderType : 'Manual')}</TableCell>
                  <TableCell>{r.patientName || '–'}</TableCell>
                  <TableCell>{r.ownerName || '–'}</TableCell>
                  <TableCell>{r.convenioName || '–'}</TableCell>
                  <TableCell>{fmtFecha(r.validoHasta)}</TableCell>
                  <TableCell align="right" sx={{ color: r.descuentoTotal ? '#DC2626' : 'text.secondary' }}>
                    {r.descuentoTotal ? `-${CLP(r.descuentoTotal)}` : '–'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(r.total)}</TableCell>
                  <TableCell><Chip size="small" label={r.estado || 'borrador'} color={ESTADO_COLOR[r.estado] || 'default'} /></TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar / Ver">
                      <IconButton size="small" onClick={() => navigate(`/presupuestos/${r.id}`)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton type="button" size="small" color="error" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(r.id); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {['aprobado', 'abonado'].includes(r.estado) && (
                      <Tooltip title={r.estado === 'abonado' ? 'Pagar saldo' : 'Cobrar'}>
                        <IconButton size="small" color="success" onClick={() => navigate(`/caja/ticket?presupuestoId=${r.id}`)}>
                          <PointOfSaleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {filtered.length > 0 && hasMore && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Cargando…' : 'Cargar más'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
